import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { avatars } from "src/assets/images"
import { selectGameConfig } from "src/store/common"
import { equipCosmetic, selectMyAvatar, selectOwnedCosmetics } from "src/store/economy"
import { addMessage } from "src/store/messages"
import { selectIsGuest } from "src/store/user"

import "./AvatarPicker.css"

/* Which picture the player is shown with (owner, 2026-10-08): the profile's row of faces. It
   lists the free four and whatever the shop sold this account, in the server's order, and sends
   the id; which slot it fills, and whether it is owned, the server decides (`equip_cosmetic`).
   The faces still for sale are the shop's shelf, which the last tile opens. */
const AvatarPicker = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const gameConfig = useSelector(selectGameConfig)
  const owned = useSelector(selectOwnedCosmetics)
  const worn = useSelector(selectMyAvatar)
  const isGuest = useSelector(selectIsGuest)
  const [busy, setBusy] = useState(null)

  const catalogue = gameConfig?.economy?.cosmetics || []
  const byId = Object.fromEntries(catalogue.map((cosmetic) => [cosmetic.id, cosmetic]))
  const order = gameConfig?.economy?.avatars || []
  const mine = order.filter((id) => byId[id]?.free || owned.includes(id))
  const forSale = order.length - mine.length

  const choose = (id) => {
    if (id === worn || busy) return
    setBusy(id)
    dispatch(equipCosmetic({ cosmetic: id }))
      .unwrap()
      .catch(() => dispatch(addMessage({ text: t("settings.avatarFailed"), type: "error" })))
      .finally(() => setBusy(null))
  }

  if (!mine.length) return null

  return (
    <div className="avatar-picker" role="radiogroup" aria-label={t("settings.avatar")}>
      {mine.map((id) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={id === worn}
          aria-label={byId[id]?.label || id}
          title={byId[id]?.label || id}
          className={`avatar-option${id === worn ? " selected" : ""}${byId[id]?.edition ? ` edition-${byId[id].edition}` : ""}`}
          disabled={busy !== null}
          onClick={() => choose(id)}
        >
          <img src={avatars[id]} alt="" />
        </button>
      ))}
      {/* The shop refuses a guest; the door is not offered to one. */}
      {forSale > 0 && !isGuest ? (
        <button
          type="button"
          className="avatar-option avatar-more"
          title={t("settings.avatarMore")}
          aria-label={t("settings.avatarMore")}
          onClick={() => ModalService.show("ShopModal", { initialTab: "looks" })}
        >
          <span aria-hidden="true">+{forSale}</span>
          <span className="avatar-more-label">{t("settings.avatarShop")}</span>
        </button>
      ) : null}
    </div>
  )
}

export default AvatarPicker
